import { AppDispatch } from '@src/redux'
import { User } from '@src/types'
import { CreateProviderSetupIntentMutation } from '@src/screens/Marketplace/graphql/createProviderSetupIntent.generated'
import { Stripe, StripeCardElement } from '@config/stripe.web'
import { dispatchAsync as createDispatchAsync } from './dispatch'

interface CreatePaymentParams {
  user: Pick<User, 'email' | 'fullName' | 'phoneNumber'>
  dispatch: AppDispatch
  card: {
    element: StripeCardElement
    stripe: Stripe
  }
}

export const createPaymentMethod = async ({ user, dispatch, card }: CreatePaymentParams) => {
  const dispatchAsync = createDispatchAsync(dispatch)
  const { fullName, phoneNumber, email } = user

  const {
    createProviderSetupIntent: { clientSecret },
  } = await dispatchAsync<CreateProviderSetupIntentMutation>({
    type: 'marketplace/createProviderSetupIntent',
    payload: {
      email,
      fullName,
      phoneNumber,
    },
  })

  const { paymentMethod, error: createError } = await card.stripe.createPaymentMethod({
    type: 'card',
    card: card.element,
    billing_details: {
      email,
      name: fullName,
      phone: phoneNumber,
    },
  })

  if (createError) {
    throw createError
  }

  if (!paymentMethod?.id || !paymentMethod.card) {
    throw new Error("Couldn't create payment method!")
  }

  const { setupIntent, error } = await card.stripe.confirmCardSetup(clientSecret, {
    payment_method: paymentMethod.id,
  })

  if (error) {
    throw error
  }

  const setupPaymentMethod = setupIntent?.payment_method
  const stripeId =
    typeof setupPaymentMethod === 'string' ? setupPaymentMethod : setupPaymentMethod?.id

  dispatch({
    type: 'app/updateUserState',
    payload: {
      paymentMethod: {
        stripeId: stripeId || paymentMethod.id,
        cardType: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        expirationDate: new Date(paymentMethod.card.exp_year, paymentMethod.card.exp_month - 1, 1),
      },
    },
  })

  return stripeId || paymentMethod.id
}
